import { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { createKnowledgeTag, type KnowledgeTag, listKnowledgeTags, loadDocumentTags, setDocumentTag } from '@/lib/knowledge-tags';

/**
 * D4 — the labels of one document, in its drawer: chips (a cross on hover for
 * whoever can edit it), « + Étiquette » to set an existing one, and for a
 * manager a new label typed in the same menu.
 */
export const DocumentTagsEditor = ({ documentId, canEdit, isManager }: { documentId: string; canEdit: boolean; isManager: boolean }) => {
  const [tags, setTags] = useState<KnowledgeTag[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([listKnowledgeTags(), loadDocumentTags([documentId])])
      .then(([all, byDocument]) => {
        if (cancelled) return;
        setTags(all);
        setSelected(byDocument[documentId] ?? []);
      })
      .catch((reason: unknown) => !cancelled && setError(reason instanceof Error ? reason.message : 'Étiquettes indisponibles.'));
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  const toggle = async (tagId: string, on: boolean) => {
    setError(null);
    try {
      await setDocumentTag(documentId, tagId, on);
      setSelected((current) => (on ? [...current, tagId] : current.filter((id) => id !== tagId)));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Étiquette non modifiée.');
    }
  };

  const createAndSet = async () => {
    if (!draft.trim()) return;
    setError(null);
    try {
      const tag = await createKnowledgeTag(draft);
      setTags((current) => [...current, tag].sort((a, b) => a.name.localeCompare(b.name, 'fr')));
      setDraft('');
      await toggle(tag.id, true);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Étiquette non créée.');
    }
  };

  const shown = tags.filter((tag) => selected.includes(tag.id));
  const available = tags.filter((tag) => !selected.includes(tag.id));
  if (!shown.length && !canEdit) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {shown.map((tag) => (
        <span key={tag.id} className="group inline-flex items-center gap-1 rounded-full bg-domino-lilac px-2 py-0.5 text-sm text-domino-aubergine">
          #{tag.name}
          {canEdit && (
            <button type="button" aria-label={`Retirer #${tag.name}`} onClick={() => void toggle(tag.id, false)}
              className="opacity-0 transition-opacity duration-150 focus:opacity-100 group-hover:opacity-100">
              <X className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
      {canEdit && (available.length > 0 || isManager) && (
        <span className="relative">
          <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-sm text-domino-inkSoft hover:text-domino-aubergine">
            <Plus className="h-3 w-3" /> Étiquette
          </button>
          {open && (
            <div className="absolute left-0 top-full z-10 mt-1 w-56 rounded-xl border border-domino-cardBorder bg-domino-white py-1 shadow-lg">
              {available.map((tag) => (
                <button key={tag.id} type="button" onClick={() => { void toggle(tag.id, true); setOpen(false); }}
                  className="block w-full px-3 py-1.5 text-left text-sm text-domino-ink hover:bg-domino-muted">
                  #{tag.name}
                </button>
              ))}
              {isManager && (
                <input
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void createAndSet();
                      setOpen(false);
                    }
                    if (event.key === 'Escape') setOpen(false);
                  }}
                  placeholder="Nouvelle étiquette"
                  aria-label="Nouvelle étiquette"
                  className="mx-2 mt-1 w-[calc(100%-1rem)] rounded-lg border border-domino-cardBorder bg-domino-warm px-2 py-1 text-sm"
                />
              )}
            </div>
          )}
        </span>
      )}
      {error && <p className="w-full text-sm text-domino-inkSoft">{error}</p>}
    </div>
  );
};
